import { Image as ImageIcon, Mic, Plus, Type, Video, X } from "lucide-react";
import { StarPicker } from "./Rating";

const KINDS = [["text", "Text", Type], ["audio", "Audio", Mic], ["video", "Video", Video], ["image", "Photo", ImageIcon]];
const ACCEPT = { audio: "audio/*", video: "video/*", image: "image/*" };
const inputCls = "w-full bg-transparent border border-border rounded-xl px-4 py-2.5 outline-none focus:border-accent transition-colors";

const PhotoPreviews = ({ tImages, setTImages, tDragIdx, setTDragIdx, moveTImage }) => (
  <>
    {tImages.length > 1 && <p className="text-xs text-muted-foreground">Drag to reorder — this is the order they'll appear in the gallery.</p>}
    <div className="flex flex-wrap gap-2" data-testid="testimonial-photo-previews">
      {tImages.map((f, idx) => (
        <div
          key={idx} draggable
          onDragStart={() => setTDragIdx(idx)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={() => moveTImage(tDragIdx, idx)}
          data-testid={`testimonial-photo-${idx}`}
          className={`relative w-20 h-20 rounded-lg overflow-hidden border cursor-move group ${tDragIdx === idx ? "border-accent opacity-60" : "border-border"}`}
        >
          <img src={URL.createObjectURL(f)} alt={`Photo ${idx + 1}`} className="w-full h-full object-cover pointer-events-none" />
          <span className="absolute top-0.5 left-0.5 grid place-items-center min-w-[1rem] h-4 px-1 rounded bg-black/60 text-white text-[10px]">{idx + 1}</span>
          <button type="button" onClick={() => setTImages((imgs) => imgs.filter((_, i) => i !== idx))} title="Remove" className="absolute top-0.5 right-0.5 grid place-items-center w-5 h-5 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={11} />
          </button>
        </div>
      ))}
    </div>
  </>
);

const MediaFields = ({ tForm, setTForm, setTFile, tImages, setTImages, tDragIdx, setTDragIdx, moveTImage }) => {
  const isImage = tForm.kind === "image";
  return (
    <div className="space-y-3">
      <input
        type="file" data-testid="testimonial-file" accept={ACCEPT[tForm.kind]} multiple={isImage}
        onChange={(e) => (isImage ? setTImages(Array.from(e.target.files || [])) : setTFile(e.target.files?.[0] || null))}
        className="text-sm text-muted-foreground file:mr-3 file:rounded-full file:border-0 file:bg-primary file:text-primary-foreground file:px-4 file:py-2 file:text-sm"
      />
      <p className="text-xs text-muted-foreground mt-1">
        {isImage ? "Select one or more photos (each up to 10MB). Multiple photos become a swipeable gallery." : "Max size: audio 15MB · video 50MB."}
        {isImage && tImages.length > 0 && <span className="text-accent"> · {tImages.length} photo(s) selected</span>}
      </p>
      {isImage && tImages.length > 0 && <PhotoPreviews tImages={tImages} setTImages={setTImages} tDragIdx={tDragIdx} setTDragIdx={setTDragIdx} moveTImage={moveTImage} />}
      {isImage && (
        <textarea data-testid="testimonial-caption" rows={2} value={tForm.text} onChange={(e) => setTForm({ ...tForm, text: e.target.value })}
          placeholder="Optional caption / short quote shown under the photo…" className={`${inputCls} resize-none`} />
      )}
    </div>
  );
};

export const TestimonialForm = ({ tForm, setTForm, setTFile, tImages, setTImages, tDragIdx, setTDragIdx, moveTImage, addTestimonial, tBusy }) => (
  <form onSubmit={addTestimonial} data-testid="testimonial-form" className="rounded-2xl border border-border bg-background/60 p-5 mb-6 space-y-4">
    <div className="flex flex-wrap gap-2">
      {KINDS.map(([k, label, Icon]) => (
        <button
          type="button" key={k} data-testid={`testimonial-kind-${k}`}
          onClick={() => { setTForm((t) => ({ ...t, kind: k })); setTFile(null); }}
          className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-1.5 text-sm transition-colors ${tForm.kind === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-white"}`}
        >
          <Icon size={14} /> {label}
        </button>
      ))}
    </div>
    <input data-testid="testimonial-name" value={tForm.name} onChange={(e) => setTForm({ ...tForm, name: e.target.value })} placeholder="Participant name" className={inputCls} />
    {tForm.kind === "text" ? (
      <textarea data-testid="testimonial-text" rows={3} value={tForm.text} onChange={(e) => setTForm({ ...tForm, text: e.target.value })}
        placeholder="Write the testimonial…" className={`${inputCls} resize-none`} />
    ) : (
      <MediaFields tForm={tForm} setTForm={setTForm} setTFile={setTFile} tImages={tImages} setTImages={setTImages} tDragIdx={tDragIdx} setTDragIdx={setTDragIdx} moveTImage={moveTImage} />
    )}
    <div className="flex flex-wrap items-center gap-6">
      <div className="flex items-center gap-2">
        <span className="text-xs uppercase tracking-widest text-muted-foreground">Rating</span>
        <StarPicker testid="testimonial-rating" value={tForm.rating} onChange={(v) => setTForm({ ...tForm, rating: v })} />
      </div>
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
        <input type="checkbox" data-testid="testimonial-featured" checked={tForm.featured} onChange={(e) => setTForm({ ...tForm, featured: e.target.checked })} className="w-4 h-4 accent-[#1A2E26]" />
        Feature at top
      </label>
    </div>
    <button type="submit" disabled={tBusy} data-testid="testimonial-add-btn" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm hover:-translate-y-0.5 transition-transform disabled:opacity-60">
      <Plus size={16} /> {tBusy ? "Adding…" : "Add testimonial"}
    </button>
  </form>
);
